/* eslint-disable @typescript-eslint/no-use-before-define */
import { LinkButton } from '@euler/app/components/LinkButton';
import { SuspenseContentWrapper } from '@euler/app/components/SuspenseContentWrapper';
import { InspectionIcon } from '@euler/app/components/icons/InspectionIcon';
import { Typography } from '@euler/app/components/typography';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { StaticTaskNavBar } from '@euler/app/flows/task/components/navbar/StaticTaskNavBar';
import {
  isInspectionTemplateNominatedFor,
  useInspectionTemplates,
} from '@euler/app/flows/task/functions';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskHeaderMetrics } from '@euler/app/flows/task/functions/useTaskHeaderMetrics';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { InspectionTemplate, VehicleInspectionFlow } from '@euler/model';
import { OrgMember } from '@euler/model/entity';
import { InspectionCategory } from '@euler/model/enum';
import { showAlert } from '@euler/utils';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  KeyboardAvoidingView,
  ListRenderItem,
  StyleProp,
  TextInput,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';
import { MemberPickerBottomSheet } from '../../../../components/MemberPickerBottomSheet';

const kSearchInputHeight = 44;

type Props = {
  category: InspectionCategory;
};

export const InspectionFlowsScreen = wrapNavigatorScreen(
  (props: Props) => {
    return (
      <SuspenseContentWrapper ErrorBoundaryComponent={null}>
        <Content {...props} />
      </SuspenseContentWrapper>
    );
  },
  { headerShown: false },
);

type Template = InspectionTemplate & { matches?: string[] };

const Content = memo(({ category }: Props) => {
  const { rpx } = useResponsiveContext();
  const { detail, taskManager, inventoryQuery } = useTaskContext();
  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const insets = useSafeAreaInsets();
  const { navBarHeight } = useTaskHeaderMetrics();

  const templates = useInspectionTemplates();
  const [keyword, setKeyword] = useState<string>('');

  const originalTemplateList = useMemo(
    () =>
      templates.filter(isInspectionTemplateNominatedFor.bind(null, category)),
    [category, templates],
  );

  const templateSearchIndex = useMemo(() => {
    const res: {
      pyInitial: string;
      name: string;
      templateId: number;
    }[] = [];
    for (const template of originalTemplateList) {
      const groups = template.conf.categories.flatMap(x => x.groups);
      for (const group of groups) {
        const disabledSiteIdSet = new Set(group.disabledSiteIds ?? []);
        const siteIds = group.siteIds.filter(x => !disabledSiteIdSet.has(x));
        for (const siteId of siteIds) {
          const site = inventoryQuery.getSiteById(siteId);
          if (site) {
            res.push({
              pyInitial: site.pyInitial ?? site.code,
              name: site.name,
              templateId: template.id,
            });
          }
        }
      }
    }
    return res;
  }, [inventoryQuery, originalTemplateList]);

  const templateList = useMemo<Template[]>(() => {
    const word = keyword.trim().toLowerCase();
    if (!word) return originalTemplateList;
    const rows = templateSearchIndex.filter(
      x => x.pyInitial.includes(word) || x.name.toLowerCase().includes(word),
    );
    const ids = new Set(rows.map(x => x.templateId));
    return originalTemplateList
      .filter(x => ids.has(x.id))
      .map(template => {
        const matches = [
          ...new Set(
            rows.filter(x => x.templateId === template.id).map(x => x.name),
          ),
        ];
        return { ...template, matches };
      });
  }, [keyword, originalTemplateList, templateSearchIndex]);

  const [currentTemplateForNewFlow, setCurrentTemplateForNewFlow] =
    useState<InspectionTemplate>();

  const onPillPress = useCallback((template: InspectionTemplate) => {
    setCurrentTemplateForNewFlow(template);
  }, []);

  const appLoading = useAppLoading();

  const onMemberSelected = useCallback(
    async (member: OrgMember) => {
      const templateId = currentTemplateForNewFlow?.id;
      if (templateId == null) return;

      setCurrentTemplateForNewFlow(undefined);

      const template = templateList.find(x => x.id === templateId);
      if (!template) return;

      const flow = detail.inspection.flows.find(
        x => x.template.originTemplateId === template.id,
      );

      await appLoading.showWithAction(async () => {
        if (flow != null) {
          if (flow.assignedTo.id === member.id) {
            return;
          }
          if (flow.status === 'finished') {
            void showAlert('提示', '该检测流程已完成，不能重新派工!');
            return;
          }
          await taskManager.inspectionManager.flowManager.updateInspectionFlowAssignee(
            flow.id,
            member.id,
          );
        } else {
          await taskManager.inspectionManager.addInspectionFlow({
            templateId: template.id,
            assignToMemberId: member.id,
          });
          navigation.goBack();
        }
      });
    },
    [
      appLoading,
      currentTemplateForNewFlow?.id,
      detail.inspection.flows,
      navigation,
      taskManager.inspectionManager,
      templateList,
    ],
  );

  const onPreviewTemplate = useCallback(
    (template: Template) => {
      navigation.push('inspectionTemplatePreview', { template });
    },
    [navigation],
  );

  const renderItem: ListRenderItem<Template> = useCallback(
    ({ item }) => {
      const flow = detail.inspection.flows.find(
        x => x.template.originTemplateId === item.id,
      );
      return (
        <Cell
          template={item}
          flow={flow}
          onPillPress={onPillPress}
          onPreview={onPreviewTemplate}
        />
      );
    },
    [detail.inspection.flows, onPillPress, onPreviewTemplate],
  );

  return (
    <>
      <KeyboardAvoidingView
        behavior="padding"
        css={`
          flex: 1;
        `}
      >
        <FlatList
          contentContainerStyle={{
            paddingHorizontal: rpx(20),
            paddingTop: navBarHeight + rpx(20),
            paddingBottom: insets.bottom + rpx(60),
          }}
          data={templateList}
          keyExtractor={x => String(x.id)}
          ListHeaderComponent={
            <SearchBox
              keyword={keyword}
              onKeywordChange={setKeyword}
              css={`
                margin-bottom: ${rpx(15)}px;
              `}
            />
          }
          renderItem={renderItem}
        />
        <StaticTaskNavBar
          title={
            category === InspectionCategory.Pre
              ? '预检流程模板'
              : '检测流程模板'
          }
        />
      </KeyboardAvoidingView>
      <MemberPickerBottomSheet
        show={currentTemplateForNewFlow != null}
        title={
          currentTemplateForNewFlow != null
            ? `为${currentTemplateForNewFlow.name}${
                detail.inspection.flows.find(
                  x =>
                    x.template.originTemplateId ===
                    currentTemplateForNewFlow.id,
                )
                  ? '重新'
                  : ''
              }派工`
            : undefined
        }
        onSelect={onMemberSelected}
        onDismiss={() => setCurrentTemplateForNewFlow(undefined)}
      />
    </>
  );
});

const Pill = memo(
  ({
    text,
    loading,
    ...props
  }: { text: string; loading?: boolean } & TouchableOpacityProps) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          width: ${rpx(68)}px;
          height: ${rpx(32)}px;
          border-radius: ${rpx(16)}px;
          align-items: center;
          justify-content: center;
        `}
        {...props}
        hitSlop={PredefinedHitSlops.large}
      >
        {loading ? (
          <ActivityIndicator size="small" color={'#fff'} />
        ) : (
          <Typography.Label color="#fff" light size={rpx(15)}>
            {text}
          </Typography.Label>
        )}
      </TouchableOpacity>
    );
  },
);

const Cell = memo(
  ({
    template,
    flow,
    loading,
    onPillPress,
    onPreview,
  }: {
    template: Template;
    flow?: VehicleInspectionFlow;
    onPillPress?: (template: Template) => void;
    onPreview?: (template: Template) => void;
    loading?: boolean;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const onPress = useCallback(() => {
      if (loading) return;
      onPillPress?.(template);
    }, [loading, onPillPress, template]);

    const onPreviewPress = useCallback(() => {
      onPreview?.(template);
    }, [onPreview, template]);

    return (
      <TouchableOpacity
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          background-color: ${theme.components.card.background};
          min-height: ${rpx(80)}px;
          border-radius: ${rpx(5)}px;
          padding: ${rpx(24)}px ${rpx(15)}px;
          margin-bottom: ${rpx(15)}px;
        `}
        onPress={onPress}
      >
        {template.icon ? (
          <Img
            uri={template.icon}
            css={`
              width: ${rpx(36)}px;
              height: ${rpx(36)}px;
              border-radius: ${rpx(8)}px;
            `}
          />
        ) : (
          <InspectionIcon size={rpx(36)} />
        )}
        <View
          css={`
            flex: 1;
            margin: 0 ${rpx(10)}px;
          `}
        >
          <Typography.Label
            size={rpx(20)}
            regular
            css={`
              line-height: ${rpx(32)}px;
            `}
          >
            {template.name}
          </Typography.Label>
          <Typography.Label size={rpx(14)} light>
            {template.description ?? '暂无该检测任务的说明'}
          </Typography.Label>
          {template.matches != null && template.matches?.length > 0 ? (
            <Typography.Label
              size={rpx(14)}
              light
              color={'#666'}
              css={`
                margin-top: ${rpx(8)}px;
              `}
            >
              {`包含${template.matches
                .slice(0, 8)
                .map(x => `"${x}"`)
                .join(', ')}等${template.matches.length}个部位`}
            </Typography.Label>
          ) : null}
          <LinkButton
            text={'查看该流程全部部位'}
            hitSlop={PredefinedHitSlops.large}
            onPress={onPreviewPress}
            icon={
              <Icon
                name="arrow-thin-right"
                css={`
                  margin-right: ${rpx(5)}px;
                `}
              />
            }
            css={`
              justify-content: flex-start;
              margin-top: ${rpx(8)}px;
            `}
          />
        </View>
        <View
          css={`
            align-items: center;
            justify-content: center;
          `}
        >
          <Pill
            onPress={onPress}
            text={flow ? '已派' : '派工'}
            loading={loading}
            css={`
              background-color: ${flow ? '#40C779' : '#207FE7'};
              margin-left: ${rpx(10)}px;
            `}
          />
          {flow ? (
            <Typography.Label
              light
              size={rpx(12)}
              color="#777"
              css={`
                margin-top: ${rpx(2)}px;
              `}
            >
              {flow.assignedTo.name}
            </Typography.Label>
          ) : null}
        </View>
      </TouchableOpacity>
    );
  },
);

const SearchBox = memo(
  ({
    keyword,
    style,
    onKeywordChange,
  }: {
    keyword: string;
    style?: StyleProp<ViewStyle>;
    onKeywordChange?: (text: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          background-color: #fff;
          height: ${rpx(kSearchInputHeight)}px;
          border-radius: ${rpx(kSearchInputHeight) / 2}px;
          border-color: #ddd;
          border-width: 1px;
          padding: 0 ${rpx(10)}px;
        `}
        style={style}
      >
        <TextInput
          placeholder="输入关键词或首字母搜索相关检测任务"
          value={keyword}
          onChangeText={onKeywordChange}
          clearButtonMode={keyword.trim() ? 'always' : 'never'}
          css={`
            flex: 1;
            font-family: ${keyword.trim()
              ? FontFamily.NotoSans.Bold
              : FontFamily.NotoSans.Regular};
            font-size: ${rpx(keyword.trim() ? 16 : 14)}px;
            color: #000;
            padding: 0 ${rpx(10)}px;
          `}
        />
      </View>
    );
  },
);
